import { getHADSItemScore, HADS_ITEMS } from '../tests/HADSTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export const getHADSClassificationLabel = (score?: number | null): string => {
  if (typeof score !== 'number') return '—';
  return score >= 9 ? 'Rastreio positivo' : 'Rastreio negativo';
};

export const getHADSClassificationTone = (score?: number | null): string => {
  if (typeof score !== 'number') return 'bg-slate-100 text-slate-700';
  return score >= 9 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700';
};

export const getHADSScoreColor = (score?: number | null): string => {
  if (typeof score !== 'number') return '#475569';
  return score >= 9 ? '#f43f5e' : '#10b981';
};

export const getHADSOverallSeverityLabel = (anxietyScore?: number | null, depressionScore?: number | null): string => {
  if (typeof anxietyScore !== 'number' && typeof depressionScore !== 'number') return '—';
  const worstScore = Math.max(anxietyScore ?? 0, depressionScore ?? 0);
  return worstScore >= 9 ? 'Rastreio positivo' : 'Rastreio negativo';
};

export const getHADSOverallSeverityTone = (anxietyScore?: number | null, depressionScore?: number | null): string => {
  if (typeof anxietyScore !== 'number' && typeof depressionScore !== 'number') return 'bg-slate-100 text-slate-700';
  const worstScore = Math.max(anxietyScore ?? 0, depressionScore ?? 0);
  return worstScore >= 9 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700';
};

export const getHADSInterpretationText = (anxietyScore?: number | null, depressionScore?: number | null): string => {
  if (typeof anxietyScore !== 'number' && typeof depressionScore !== 'number') {
    return 'Não há pontuação suficiente para interpretar o HADS.';
  }
  const anxietyClass = getHADSClassificationLabel(anxietyScore).toLowerCase();
  const depressionClass = getHADSClassificationLabel(depressionScore).toLowerCase();
  const worstScore = Math.max(anxietyScore ?? 0, depressionScore ?? 0);

  if (worstScore < 9) {
    return 'A triagem brasileira apresenta resultado negativo nos dois domínios do HADS. O instrumento não substitui avaliação diagnóstica.';
  }

  return `A triagem brasileira apresenta resultado ${anxietyClass} para ansiedade e ${depressionClass} para depressão. Resultado positivo (≥ 9 em qualquer domínio) indica necessidade de avaliação clínica mais aprofundada, sem estabelecer diagnóstico.`;
};

const getMarkerPositionPercent = (score?: number | null): number | null => {
  if (typeof score !== 'number') return null;
  return (Math.max(0, Math.min(21, score)) / 21) * 100;
};

interface HADSResultRendererProps {
  payload:
    | {
        answers?: unknown;
        anxietyScore?: unknown;
        depressionScore?: unknown;
        instrumentVersion?: unknown;
      }
    | null
    | undefined;
}

interface HADSScoreCardProps {
  title: string;
  score: number | null;
}

function HADSScoreCard({ title, score }: HADSScoreCardProps) {
  const classification = getHADSClassificationLabel(score);
  const scoreColor = getHADSScoreColor(score);
  const markerPosition = getMarkerPositionPercent(score);

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
      <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">{title}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="text-7xl font-black leading-none" style={{ color: scoreColor }}>
          {score ?? '—'}
        </div>
        <div className="mb-2 inline-flex items-center gap-2 text-sm font-bold" style={{ color: scoreColor }}>
          <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
          {classification}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="relative py-1">
          <div
            className="h-8 rounded-full"
            style={{
              background: 'linear-gradient(90deg, #22c55e 0%, #22c55e 42.8%, #ef4444 42.9%, #ef4444 100%)',
            }}
          />
          {markerPosition != null && (
            <div className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800" style={{ left: `calc(${markerPosition}% - 2px)` }} />
          )}
        </div>
        <div className="flex items-center justify-between text-[11px] font-medium text-slate-500">
          <span>0-8: negativo</span>
          <span>9-21: positivo</span>
        </div>
      </div>
    </div>
  );
}

export default function HADSResultRenderer({ payload }: HADSResultRendererProps) {
  const answers = Array.isArray(payload?.answers) ? payload.answers : [];
  const isLegacyScoring = payload?.instrumentVersion !== 'HADS_BR_BOTEGA_1995';
  const anxietyScore = typeof payload?.anxietyScore === 'number' ? Math.max(0, Math.min(21, payload.anxietyScore)) : null;
  const depressionScore = typeof payload?.depressionScore === 'number' ? Math.max(0, Math.min(21, payload.depressionScore)) : null;
  const interpretation = resolveAssessmentInterpretationText(
    payload,
    getHADSInterpretationText(anxietyScore, depressionScore),
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {isLegacyScoring ? (
          <div className="rounded-[28px] border border-amber-200 bg-amber-50 p-6 text-sm leading-relaxed text-amber-900 md:col-span-2">
            Resultado legado: a versão registrada não identifica a chave validada de pontuação item a item. As respostas e pontuações originais são preservadas, mas não recebem classificação automática.
          </div>
        ) : (
          <>
            <HADSScoreCard title="Ansiedade" score={anxietyScore} />
            <HADSScoreCard title="Depressão" score={depressionScore} />
          </>
        )}
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
        <p className="mt-3 max-w-4xl text-sm leading-relaxed text-slate-600">
          {isLegacyScoring ? 'Resultado mantido apenas para consulta histórica, sem reclassificação retroativa.' : interpretation}
        </p>
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
          Referência brasileira: Botega et al. (1995), com rastreio positivo a partir de 9 pontos em cada subescala. O HADS não substitui diagnóstico clínico.
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Questão</th>
              <th className="px-6 py-4">Domínio</th>
              <th className="px-6 py-4">Resposta selecionada</th>
              <th className="px-6 py-4 text-center">Pontuação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {HADS_ITEMS.map((item, index) => {
              const rawValue = answers[index];
              const optionIndex = typeof rawValue === 'number' && rawValue >= 0 ? rawValue : null;
              const score = optionIndex != null && !isLegacyScoring
                ? getHADSItemScore(index, optionIndex)
                : null;
              const selectedOption = optionIndex != null ? item.options[optionIndex] : 'Não respondido';
              const domainLabel = item.domain === 'A' ? 'Ansiedade' : 'Depressão';
              return (
                <tr key={`${item.domain}-${index}`} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-medium text-slate-700">{index + 1}. {item.question}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-lg px-2.5 py-1 text-[10px] font-black uppercase ${
                      item.domain === 'A' ? 'bg-blue-100 text-blue-700' : 'bg-fuchsia-100 text-fuchsia-700'
                    }`}>
                      {domainLabel}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{selectedOption}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">
                    {isLegacyScoring ? 'Legado' : score ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
